import type { CmmdTaskStatus } from "@cmmd-forge/contracts";
import {
  KanbanSquareIcon,
  PanelsTopLeftIcon,
  RefreshCwIcon,
  SearchIcon,
  SlidersHorizontalIcon,
} from "lucide-react";

import { cn } from "~/lib/utils";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import {
  AssigneeSelect,
  FilterSelect,
  STATUS_OPTIONS,
  type FilterOption,
} from "./FilterBarControls";

export type CommandCenterView = "control" | "board";

export interface FilterBarWorkspace {
  id: string;
  name: string;
}

export interface FilterBarProps {
  scope?: "global" | "project";
  workspaces: FilterBarWorkspace[];
  activeWorkspaceId: string | null;
  searchQuery: string;
  /** When true, the list/board is filtered to tasks that are actionable now (isReady === true). */
  readyOnly: boolean;
  /** Count of currently-ready items in the full (unfiltered) set — shown next to the toggle. */
  readyCount: number;
  teamFilter: string;
  teamOptions: ReadonlyArray<FilterOption>;
  campaignFilter: string;
  campaignOptions: ReadonlyArray<FilterOption>;
  projectFilter: string;
  projectOptions: ReadonlyArray<FilterOption>;
  statusFilter: CmmdTaskStatus | "all";
  assigneeFilter: string;
  assigneeOptions: ReadonlyArray<FilterOption>;
  isRefreshing?: boolean;
  view: CommandCenterView;
  onViewChange: (view: CommandCenterView) => void;
  onRefresh?: () => void;
  onWorkspaceChange: (workspaceId: string | null) => void;
  onSearchQueryChange: (value: string) => void;
  onReadyOnlyChange: (next: boolean) => void;
  onTeamFilterChange: (value: string) => void;
  onCampaignFilterChange: (value: string) => void;
  onProjectFilterChange: (value: string) => void;
  onStatusFilterChange: (value: CmmdTaskStatus | "all") => void;
  onAssigneeFilterChange: (value: string) => void;
}

const CONTROL_SURFACE = cn(
  "h-7 rounded-md border border-border/45 bg-background/20 text-xs",
  "text-foreground/90 transition-colors hover:bg-muted/25 hover:border-border/65",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
);

const VIEW_BTN = cn(
  "inline-flex size-6 items-center justify-center rounded-sm text-muted-foreground/75 transition-colors",
  "hover:text-foreground hover:bg-muted/55",
  "data-[active=true]:bg-muted data-[active=true]:text-foreground",
);

export interface ViewSwitcherProps {
  view: CommandCenterView;
  onViewChange: (view: CommandCenterView) => void;
}

export function ViewSwitcher({ view, onViewChange }: ViewSwitcherProps) {
  return (
    <div className="inline-flex h-7 shrink-0 items-center rounded-md border border-border/45 bg-background/20 p-0.5">
      <button
        type="button"
        className={VIEW_BTN}
        data-active={view === "control"}
        title="Control view"
        onClick={() => onViewChange("control")}
        aria-label="Control view"
      >
        <PanelsTopLeftIcon aria-hidden="true" className="size-3.5" />
      </button>
      <button
        type="button"
        className={VIEW_BTN}
        data-active={view === "board"}
        title="Kanban view"
        onClick={() => onViewChange("board")}
        aria-label="Kanban view"
      >
        <KanbanSquareIcon aria-hidden="true" className="size-3.5" />
      </button>
    </div>
  );
}

export function FilterBar(props: FilterBarProps) {
  const {
    scope = "global",
    workspaces,
    activeWorkspaceId,
    searchQuery,
    teamFilter,
    teamOptions,
    campaignFilter,
    campaignOptions,
    projectFilter,
    projectOptions,
    statusFilter,
    assigneeFilter,
    assigneeOptions,
    isRefreshing = false,
    view,
    onViewChange,
    onRefresh,
    onWorkspaceChange,
    onSearchQueryChange,
    onTeamFilterChange,
    onCampaignFilterChange,
    onProjectFilterChange,
    onStatusFilterChange,
    onAssigneeFilterChange,
  } = props;

  const workspaceOptions: ReadonlyArray<FilterOption> = [
    { value: "all", label: "All projects" },
    ...workspaces.map((workspace) => ({ value: workspace.id, label: workspace.name })),
  ];
  const activeAdvancedFilterCount =
    (scope === "global" && activeWorkspaceId !== null ? 1 : 0) +
    (scope === "global" && teamFilter !== "all" ? 1 : 0) +
    (scope === "global" && campaignFilter !== "all" ? 1 : 0) +
    (scope === "global" && projectFilter !== "all" ? 1 : 0) +
    (statusFilter !== "all" ? 1 : 0) +
    (assigneeFilter !== "all" ? 1 : 0);

  return (
    <div className="px-4 pt-3 pb-2 sm:px-5">
      <div className="grid gap-2 rounded-lg border border-border/35 bg-card/15 p-2 md:grid-cols-[minmax(240px,1fr)_auto] md:items-center">
        <label className={cn(CONTROL_SURFACE, "inline-flex min-w-0 items-center gap-1.5 px-2")}>
          <SearchIcon className="size-3.5 shrink-0 text-muted-foreground/70" />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => onSearchQueryChange(event.currentTarget.value)}
            placeholder="Search tasks"
            aria-label="Search Projects tasks"
            className="h-full min-w-0 flex-1 bg-transparent text-xs font-medium text-foreground/95 outline-none placeholder:text-muted-foreground/45"
          />
        </label>

        <div className="flex min-w-0 flex-wrap items-center gap-2 md:justify-end">
          <Popover>
            <PopoverTrigger
              className={cn(CONTROL_SURFACE, "inline-flex shrink-0 items-center gap-1.5 px-2")}
              aria-label="Additional filters"
              aria-describedby="command-center-additional-filters-description"
              title="Campaign, project, and assignee filters"
            >
              <SlidersHorizontalIcon className="size-3.5 text-muted-foreground/70" />
              <span className="text-xs font-medium">Filters</span>
              <span id="command-center-additional-filters-description" className="sr-only">
                {scope === "global"
                  ? "Project filter, Team filter, Campaign filter, Project filter, Status filter, Assignee filter"
                  : "Status filter, Assignee filter"}
              </span>
              {activeAdvancedFilterCount > 0 ? (
                <span className="font-mono text-xs tabular-nums text-muted-foreground/80">
                  {activeAdvancedFilterCount}
                </span>
              ) : null}
            </PopoverTrigger>
            <PopoverPopup side="bottom" align="end" className="w-[280px]">
              <div className="flex flex-col gap-3">
                {scope === "global" ? (
                  <>
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground/70">
                        Workspace
                      </p>
                      <FilterSelect
                        label="Workspace"
                        ariaLabel="Project filter"
                        value={activeWorkspaceId ?? "all"}
                        options={workspaceOptions}
                        onChange={(next) => onWorkspaceChange(next === "all" ? null : next)}
                        widthClassName="w-full"
                        controlClassName={CONTROL_SURFACE}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground/70">
                        Team
                      </p>
                      <FilterSelect
                        label="Team"
                        ariaLabel="Team filter"
                        value={teamFilter}
                        options={teamOptions}
                        onChange={onTeamFilterChange}
                        widthClassName="w-full"
                        controlClassName={CONTROL_SURFACE}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground/70">
                        Campaign
                      </p>
                      <FilterSelect
                        label="Campaign"
                        ariaLabel="Campaign filter"
                        value={campaignFilter}
                        options={campaignOptions}
                        onChange={onCampaignFilterChange}
                        widthClassName="w-full"
                        controlClassName={CONTROL_SURFACE}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground/70">
                        Project
                      </p>
                      <FilterSelect
                        label="Project"
                        ariaLabel="Project filter"
                        value={projectFilter}
                        options={projectOptions}
                        onChange={onProjectFilterChange}
                        widthClassName="w-full"
                        controlClassName={CONTROL_SURFACE}
                      />
                    </div>
                  </>
                ) : null}

                <div className="space-y-1.5">
                  <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground/70">
                    Status
                  </p>
                  <FilterSelect
                    label="Status"
                    ariaLabel="Status filter"
                    value={statusFilter}
                    options={STATUS_OPTIONS}
                    onChange={(next) => onStatusFilterChange(next as CmmdTaskStatus | "all")}
                    widthClassName="w-full"
                    controlClassName={CONTROL_SURFACE}
                  />
                </div>

                <div className="space-y-1.5">
                  <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground/70">
                    Assignee
                  </p>
                  <AssigneeSelect
                    value={assigneeFilter}
                    options={assigneeOptions}
                    onChange={onAssigneeFilterChange}
                    widthClassName="w-full"
                    controlClassName={CONTROL_SURFACE}
                  />
                </div>
              </div>
            </PopoverPopup>
          </Popover>

          <button
            type="button"
            className={cn(CONTROL_SURFACE, "inline-flex shrink-0 items-center gap-1.5 px-2")}
            title={isRefreshing ? "Refreshing CMMD data" : "Refresh CMMD data"}
            aria-label="Refresh CMMD data"
            disabled={!onRefresh || isRefreshing}
            onClick={onRefresh}
          >
            <RefreshCwIcon
              aria-hidden="true"
              className={cn("size-3.5 text-muted-foreground/70", isRefreshing && "animate-spin")}
            />
            <span className="text-xs font-medium">Refresh</span>
          </button>
          <ViewSwitcher view={view} onViewChange={onViewChange} />
        </div>
      </div>
    </div>
  );
}
